import { KgInsightThemeType } from '@api/api/kg-insights';
import { ThemePerformanceBasket } from '@api/api/theme-overview';
import { useGetThemePerformance } from '@terminal/react-query-hooks/themes/useGetThemePerformance';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useThemePerformanceChartData } from './useThemePerformanceChartData';

type PerformanceData = ThemePerformanceBasket['performance'];

export const getReturnValue = ({
  value,
  benchmarkValue,
  withBenchmark,
}: {
  value: number | undefined;
  benchmarkValue?: number;
  withBenchmark?: boolean;
}) => {
  if (withBenchmark) {
    if (benchmarkValue !== undefined && value !== undefined) {
      return value - benchmarkValue;
    } else {
      return undefined;
    }
  }

  return value;
};

export const useThemePerformanceReturn = ({
  themeId,
  themeType,
}: {
  themeId: string;
  themeType?: KgInsightThemeType;
}) => {
  const isIntradayChartEnabled = useFlags(state =>
    state.getFeatureFlagState('theme_intraday_chart')
  );

  const { themes, isLoading } = useGetThemePerformance({ ids: themeId });
  const theme = themes?.[0];

  const { chartData: intradayData } = useThemePerformanceChartData({
    themeId,
    themeType,
    horizon: 'intraday',
    enabled: isIntradayChartEnabled && !!themeId,
  });

  const performanceData = theme?.performance;
  const headlineHorizon = theme?.intraday_metadata.headline_horizon;
  const intradayReturnValue = intradayData?.aggregate?.at(-1)?.value;

  const getReturnForHorizon = (horizon: string) => {
    const horizonKey = horizon.toLowerCase() as keyof PerformanceData;

    if (horizon === '1D') {
      return intradayReturnValue ?? performanceData?.[headlineHorizon ?? '1d'];
    }

    return performanceData?.[horizonKey];
  };

  return {
    performanceData,
    headlineHorizon,
    intradayReturnValue,
    isLoading,
    getReturnForHorizon,
    getReturnValue,
  };
};
